import { Navigate, Outlet, RouteObject, RouterProvider } from "react-router";
import { createBrowserRouter } from "react-router-dom";
import { AuthorizationGate } from "./components/authorization-gate/authorization-gate";
import { PriceList, Information, Profile, FAQ, ContactInfo, ProductDetails, ProjectList, Configurator, ConfigureProduct, BulkConfigureProducts } from "./pages";
import { ApplicationWrapper } from "./components";
import { ROUTES_URL } from "./utils/constants";
import { useGetContactPersonDetail } from "./hooks";
import { Shared_ContactPersonPermissionKinds } from "./backend/openapi/v1";
import { useMemo } from "react";
import NotFoundPage from "./pages/not-found/not-found";

type AppRouteObject = Omit<RouteObject, "children"> & {
  requiredPermission?: Shared_ContactPersonPermissionKinds;
  children?: AppRouteObject[];
};

const PageWrapper = () => (
  <AuthorizationGate>
    <Outlet />
  </AuthorizationGate>
);

export const RootRoutes = () => {
  const { contactPersonDetail } = useGetContactPersonDetail();
  const permissions = contactPersonDetail?.permissions || [];

  const takeFirstAvailableRoute = (routes: AppRouteObject[]) => {
    const route = routes.find(r => !r.requiredPermission || permissions.includes(r.requiredPermission));
    return route?.path ?? "/not-found";
  };

  const checkRoutePermission = (routes: AppRouteObject[]): RouteObject[] => {
    return routes.map((route): RouteObject => {
      const { requiredPermission, children, ...rest } = route;

      const element = requiredPermission && !permissions.includes(requiredPermission) ? <Navigate to="/not-found" replace /> : rest.element;

      const newRoute: RouteObject = {
        ...rest,
        element
      };

      if (children) {
        newRoute.children = checkRoutePermission(children);
      }

      return newRoute;
    });
  };

  const generalRoutes: AppRouteObject[] = [
    {
      path: ROUTES_URL.HOME,
      element: <ApplicationWrapper />,
      children: [
        {
          path: ROUTES_URL.PROJECTS,
          requiredPermission: Shared_ContactPersonPermissionKinds.ViewProjects,
          element: <Outlet />,
          children: [
            { index: true, element: <ProjectList /> },
            {
              path: ROUTES_URL.CONFIGURATOR.pattern,
              element: <Configurator />,
              // BULK_CONFIGURE_PRODUCTS is listed first for readability; react-router ranks its static
              // "bulk" segment above CONFIGURE_PRODUCT's ":configurationId" either way
              children: [
                { path: ROUTES_URL.BULK_CONFIGURE_PRODUCTS.pattern, element: <BulkConfigureProducts /> },
                { path: ROUTES_URL.CONFIGURE_PRODUCT.pattern, element: <ConfigureProduct /> }
              ]
            }
          ]
        },
        { path: ROUTES_URL.INFORMATION.root, element: <Information /> },
        { path: ROUTES_URL.PRICE_LIST, element: <PriceList />, requiredPermission: Shared_ContactPersonPermissionKinds.ViewPricelist },
        { path: ROUTES_URL.CONTACT, element: <ContactInfo /> },
        { path: ROUTES_URL.FAQ, element: <FAQ /> },
        { path: ROUTES_URL.PROFILE, element: <Profile /> },
        { path: ROUTES_URL.PRODUCT_DETAILS.pattern, element: <ProductDetails /> },
        { path: ROUTES_URL.INFORMATION.pattern, element: <Information /> }
      ]
    }
  ];

  if (generalRoutes[0].children) {
    generalRoutes[0].children.unshift({
      index: true,
      element: <Navigate to={takeFirstAvailableRoute(generalRoutes[0].children)} replace />
    });
  }

  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: "/",
          element: <PageWrapper />,
          children: [...checkRoutePermission(generalRoutes), { path: "*", element: <NotFoundPage /> }]
        }
      ]),
    [permissions]
  );

  return (
    <RouterProvider
      future={{
        v7_startTransition: true
      }}
      router={router}
    />
  );
};
